import React, { useState, useRef, useEffect } from 'react';
import './PlayerNameInput.css';

interface PlayerNameInputProps {
  currentName: string;
  onNameChange: (name: string) => Promise<void>;
  disabled?: boolean;
  label?: string;
}

const PlayerNameInput: React.FC<PlayerNameInputProps> = ({
  currentName,
  onNameChange,
  disabled = false,
  label = 'Player or team name'
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(currentName);
  const [isLoading, setIsLoading] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setEditValue(currentName);
  }, [currentName]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleClick = () => {
    if (!disabled && !isLoading) {
      setIsEditing(true);
      setEditValue(currentName);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  const handleSave = async () => {
    const trimmedValue = editValue.trim();
    
    if (trimmedValue === currentName) {
      setIsEditing(false);
      return;
    }

    if (trimmedValue.length > 16) {
      return; // Don't save if over 8 characters
    }

    setIsLoading(true);
    try {
      await onNameChange(trimmedValue);
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to update name:', error);
      // Reset to current name on error
      setEditValue(currentName);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setEditValue(currentName);
    setIsEditing(false);
  };

  const handleBlur = () => {
    // Small delay to allow clicking save button
    setTimeout(() => {
      if (isEditing) {
        handleSave();
      }
    }, 100);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value.length <= 16) {
      setEditValue(value);
    }
  };

  const displayValue = currentName || 'Set your name';
  const isOverLimit = editValue.length > 16;

  return (
    <div className="player-name-container">
      <div className="player-name-section">
        <label className="player-name-label">{label}</label>
        <div className="player-name-wrapper">
          {isEditing ? (
            <div className="player-name-edit">
              <input
                ref={inputRef}
                type="text"
                value={editValue}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                onBlur={handleBlur}
                className={`player-name-input ${isOverLimit ? 'invalid' : ''}`}
                data-testid="player-name-input"
                placeholder="Enter name (max 16 chars)"
                maxLength={16}
                disabled={isLoading}
              />
              <div className="player-name-actions">
                <button
                  onClick={handleSave}
                  disabled={isLoading || isOverLimit || editValue.trim() === currentName}
                  className="save-button"
                  data-testid="player-name-save"
                  title="Save name"
                >
                  {isLoading ? '⏳' : '✓'}
                </button>
                <button
                  onClick={handleCancel}
                  disabled={isLoading}
                  className="cancel-button"
                  title="Cancel"
                >
                  ✕
                </button>
              </div>
            </div>
          ) : (
            <div
              className={`player-name-display ${!currentName ? 'placeholder' : ''} ${disabled ? 'disabled' : ''}`}
              data-testid="player-name-display"
              onClick={handleClick}
              onMouseEnter={() => setShowTooltip(true)}
              onMouseLeave={() => setShowTooltip(false)}
              title={!currentName ? 'Click to set your name' : 'Click to edit name'}
            >
              {displayValue}
              {!disabled && <span className="edit-hint">✏️</span>}
            </div>
          )}
        </div>
      </div>
      
      {showTooltip && !isEditing && (
        <div className="player-name-tooltip">
          {currentName 
            ? 'Click to edit your name'
            : 'Set your name to appear on the leaderboard if you complete enough pubs or get a good time!'
          }
        </div>
      )}
    </div>
  );
};

export default PlayerNameInput;
